import React, { useContext, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Trans } from 'react-i18next';
import { CompanyContext } from 'contexts/CompanyContext';
import { CompanyOnboardingContext } from 'contexts/CompanyOnboardingContext';
import CreatableSelect from 'react-select/creatable';
import { Select } from 'components/ui';
import { CustomControl, CustomSelectOption } from 'views/company_settings/components/CompanyProfile';
import { languageOptions } from 'views/onboarding/StepCompanyLanguage';
import { useWhitelabel } from 'contexts/WhitelabelThemeContext';
import { TbSpeakerphone, TbLanguage, TbTarget, TbMicrophone, TbUsers } from 'react-icons/tb';
import useDarkMode from 'utils/hooks/useDarkMode';

export default function StepCompanyBrandVoice() {
  const { company } = useContext(CompanyContext);
  const { setCurrentStepData } = useContext(CompanyOnboardingContext);
  const { whitelabel } = useWhitelabel();
  const [isDarkMode] = useDarkMode();
  const [interests, setInterests] = useState(company?.interests || []);
  const [audience_types, setAudienceTypes] = useState(company?.audience_types || []);

  const [ tones, setTones ] = useState(company?.tones || []);
  const [ emotions, setEmotions ] = useState(company?.emotions || []);
  const [ syntaxes, setSyntaxes ] = useState(company?.syntaxes || []);
  const [ language_types, setLanguageTypes ] = useState(Array.isArray(company?.language_types) ? company?.language_types : []);
  const [ language_iso_code, setLanguageIsoCode ] = useState(company?.language_iso_code || '');
  const [ language, setLanguage ] = useState(company?.language || '');
  const [ characters, setCharacters ] = useState(company?.characters || []);

  const [ audienceTypesOptions, setAudienceTypesOptions ] = useState((Array.isArray(audience_types) ? audience_types : []).map(e => ({ value: e, label: e?.length > 50 ? e?.slice(0, 50) + '...' : e })));
  const [ interestsOptions, setInterestsOptions ] = useState((Array.isArray(interests) ? interests : []).map(e => ({ value: e, label: e?.length > 50 ? e?.slice(0, 50) + '...' : e })));
  const [ tonesOptions, setTonesOptions ] = useState((Array.isArray(tones) ? tones : []).map(e => ({ value: e, label: e?.length > 50 ? e?.slice(0, 50) + '...' : e })));
  const [ emotionsOptions, setEmotionsOptions ] = useState((Array.isArray(emotions) ? emotions : []).map(e => ({ value: e, label: e?.length > 50 ? e?.slice(0, 50) + '...' : e })));
  const [ syntaxesOptions, setSyntaxesOptions ] = useState((Array.isArray(syntaxes) ? syntaxes : []).map(e => ({ value: e, label: e?.length > 50 ? e?.slice(0, 50) + '...' : e })));
  const [ languageTypesOptions, setLanguageTypesOptions ] = useState((Array.isArray(language_types) ? language_types : []).map(e => ({ value: e, label: e?.length > 50 ? e?.slice(0, 50) + '...' : e })));
  const [ charactersOptions, setCharactersOptions ] = useState((Array.isArray(characters) ? characters : []).map(e => ({ value: e, label: e?.length > 50 ? e?.slice(0, 50) + '...' : e })));

  // Update the context with current data
  useEffect(() => {
    setCurrentStepData({
      language_iso_code: language_iso_code || 'en',
      language: language || 'English (US)',
      interests: interests?.length ? interests : [],
      audience_types: audience_types?.length ? audience_types : [],
      tones: tones?.length ? tones : [],
      emotions: emotions?.length ? emotions : [],
      syntaxes: syntaxes?.length ? syntaxes : [],
      language_types: language_types?.length ? language_types : [],
      characters: characters?.length ? characters : []
    });
  }, [language_iso_code, language, interests, audience_types, tones, emotions, syntaxes, language_types, characters, setCurrentStepData]);

  return (
    <>
      <div className="flex flex-col gap-4 md:gap-6 flex-1 overflow-y-auto">
        {/* Animated Icon */}
        <motion.div
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ type: "spring", stiffness: 200, damping: 20 }}
          className="flex justify-center mb-2 md:mb-4"
        >
          <div className="w-16 h-16 md:w-20 md:h-20 lg:w-24 lg:h-24 rounded-2xl md:rounded-3xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center shadow-2xl shadow-orange-500/50">
            <TbSpeakerphone className="text-3xl md:text-4xl lg:text-5xl text-white" />
          </div>
        </motion.div>

        {/* Title */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="text-center"
        >
          <h3 className={`text-2xl md:text-3xl lg:text-4xl font-black mb-2 md:mb-3 ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>
            Define Your Brand Voice
          </h3>
          <p className={`text-base md:text-lg ${
            isDarkMode ? 'text-gray-400' : 'text-gray-600'
          }`}>
            Help our AI match your communication style
          </p>
        </motion.div>

        {/* Form Fields */}
        <div className='flex flex-col gap-4 md:gap-5'>
          {/* Language */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className='space-y-1.5 md:space-y-2'
          >
            <div className="flex items-center gap-2">
              <TbLanguage className={`text-lg md:text-xl ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
              <label className={`font-bold text-xs md:text-sm ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Language
              </label>
            </div>
            <Select
              options={languageOptions}
              components={{
                Option: CustomSelectOption,
                Control: CustomControl,
              }}
              value={languageOptions.find((option) => option.value === language) || languageOptions.find((option) => option.iso_code === language_iso_code)}
              onChange={(option) => {
                setLanguage(option.value);
                setLanguageIsoCode(option.iso_code);
              }}
            />
          </motion.div>

          {/* Topics */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className='space-y-1.5 md:space-y-2'
          >
            <div className="flex items-center gap-2">
              <TbTarget className={`text-lg md:text-xl ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`} />
              <label className={`font-bold text-xs md:text-sm ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Topics & Interests
              </label>
            </div>
            <Select
              isMulti
              options={interestsOptions}
              componentAs={CreatableSelect}
              className='hide-indicators'
              placeholder='Type and press Enter to add...'
              value={interests?.map(e => ({ value: e, label: e }))}
              ClearIndicator={() => null}
              onInputChange={(_, action) => {
                if (action.action == 'set-value') {
                  setInterestsOptions([...interestsOptions, { value: action.prevInputValue, label: action.prevInputValue }]);
                }
              }}
              onChange={(option, action) => {
                if (action.action == 'remove-value') {
                  setInterestsOptions(interestsOptions.filter(e => e.value != action.removedValue.value));
                }
                setInterests(option.map(item => item.value));
              }}
            />
          </motion.div>

          {/* Audiences */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4 }}
            className='space-y-1.5 md:space-y-2'
          >
            <div className="flex items-center gap-2">
              <TbUsers className={`text-lg md:text-xl ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} />
              <label className={`font-bold text-xs md:text-sm ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Target Audiences
              </label>
            </div>
            <Select
              isMulti
              options={audienceTypesOptions}
              componentAs={CreatableSelect}
              className='hide-indicators'
              placeholder='Type and press Enter to add...'
              value={audience_types?.map(e => ({ value: e, label: e }))}
              ClearIndicator={() => null}
              onInputChange={(_, action) => {
                if (action.action == 'set-value') {
                  setAudienceTypesOptions([...audienceTypesOptions, { value: action.prevInputValue, label: action.prevInputValue }]);
                }
              }}
              onChange={(option, action) => {
                if (action.action == 'remove-value') {
                  setAudienceTypesOptions(audienceTypesOptions.filter(e => e.value != action.removedValue.value));
                }
                setAudienceTypes(option.map(item => item.value));
              }}
            />
          </motion.div>

          {/* Tones */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 }}
            className='space-y-1.5 md:space-y-2'
          >
            <div className="flex items-center gap-2">
              <TbMicrophone className={`text-lg md:text-xl ${isDarkMode ? 'text-pink-400' : 'text-pink-600'}`} />
              <label className={`font-bold text-xs md:text-sm ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Communication Tones
              </label>
            </div>
            <Select
              isMulti
              options={tonesOptions}
              componentAs={CreatableSelect}
              className='hide-indicators'
              placeholder='Type and press Enter to add...'
              value={tones?.map(e => ({ value: e, label: e }))}
              ClearIndicator={() => null}
              onInputChange={(_, action) => {
                if (action.action == 'set-value') {
                  setTonesOptions([...tonesOptions, { value: action.prevInputValue, label: action.prevInputValue }]);
                }
              }}
              onChange={(option, action) => {
                if (action.action == 'remove-value') {
                  setTonesOptions(tonesOptions.filter(e => e.value != action.removedValue.value));
                }
                setTones(option.map(item => item.value));
              }}
            />
          </motion.div>

          {/* Language Types */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.6 }}
            className='space-y-1.5 md:space-y-2 hidden md:block'
          >
            <div className="flex items-center gap-2">
              <TbLanguage className={`text-lg md:text-xl ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`} />
              <label className={`font-bold text-xs md:text-sm ${
                isDarkMode ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Language Style
              </label>
            </div>
            <Select
              isMulti
              options={languageTypesOptions}
              componentAs={CreatableSelect}
              className='hide-indicators'
              placeholder='Type and press Enter to add...'
              value={language_types?.map(e => ({ value: e, label: e }))}
              ClearIndicator={() => null}
              onInputChange={(_, action) => {
                if (action.action == 'set-value') {
                  setLanguageTypesOptions([...languageTypesOptions, { value: action.prevInputValue, label: action.prevInputValue }]);
                }
              }}
              onChange={(option, action) => {
                if (action.action == 'remove-value') {
                  setLanguageTypesOptions(languageTypesOptions.filter(e => e.value != action.removedValue.value));
                }
                setLanguageTypes(option.map(item => item.value));
              }}
            />
          </motion.div>
        </div>

        {/* Info Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className={`p-3 md:p-4 rounded-xl md:rounded-2xl ${
            isDarkMode 
              ? 'bg-orange-500/10 border border-orange-500/20' 
              : 'bg-orange-50 border border-orange-200'
          }`}
        >
          <p className={`text-xs md:text-sm ${
            isDarkMode ? 'text-orange-300' : 'text-orange-700'
          }`}>
            💡 <strong>Tip:</strong> The more specific you are about your brand voice, the more personalized your AI-generated content will be
          </p>
        </motion.div>
      </div>
    </>
  );
}
